import styled from 'styled-components';
import {
  BodyText,
  Checkbox,
  FlexColumn,
  FlexRow,
  Input,
  SvgIcon,
  Textarea
} from '@make-software/cspr-design';
import Select from 'react-select';
import React, { useState } from 'react';
import {
  ErrorText,
  ErrorTextContainer,
  FormContainer,
  StyledConfirmButton,
  StyledDismissButton,
  TOGGLE_ACTIVE_COLOR,
  AppsFiltersStyles
} from 'app/components/common/common-styles';
import { Offer, updateOffer, DEAL_TYPE_OPTIONS } from 'app/console-api';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { editOfferValidationSchema } from 'app/components/management/apps/offers/common/offer-validation-schema';
import { generatePath, useNavigate } from 'react-router-dom';
import Switch from 'react-switch';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, statusCode } from 'app/constants';
import {
  APPS_OFFERS_PATH,
  APPS_OFFERS_VIEW_PATH,
  APPS_TASKS_VIEW_PATH,
  SIGN_IN_PATH
} from 'app/route/paths';
import SuccessEntityWindow from 'app/components/common/success-entity-modal/success-entity-modal';
import CreateSuccessIcon from 'assets/logos/success-entity.svg';
import { ErrorResult } from 'app/organizations-api/types';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import ErrorEntityWindow from 'app/components/common/error-entity-modal/error-entity-modal';
import { useTabsCtx } from 'app/components/management/apps/tabs-page-layout';
import { DealTradeInfoSnippet } from 'app/components/common/deal-trade-type/deal-trade-info-snppet';

const StyledSeparator = styled.div<{ margin?: string }>(({ theme, margin }) =>
  theme.withMedia({
    borderBottom: '1px solid ' + theme.styleguideColors.borderPrimary,
    margin: margin ? `${margin} 0` : '16px 0'
  })
);

const ItemArea = styled(FlexColumn)<{ $withScroll?: boolean }>(({ theme, $withScroll }) =>
  theme.withMedia({
    overflow: $withScroll ? 'scroll' : 'unset',
    borderRadius: '4px',
    background: '#F5F5F7',
    padding: '16px'
  })
);

export const StyledInput = styled(Input)<{ withBorder?: boolean }>(({ theme, withBorder }) =>
  theme.withMedia({
    width: '100%',
    'div:nth-child(2)': {
      border: withBorder ? `1px solid ${theme.styleguideColors.contentSecondary}` : 'none'
    }
  })
);

const StyledButtonRow = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    margin: '40px 0 0 0'
  })
);

export const StyledCaption = styled.div<{ margin?: string }>(({ theme, margin }) =>
  theme.withMedia({
    margin: margin ? margin : '0 0 20px 0'
  })
);

interface OfferEditProps {
  offer: Offer;
}

export const OfferEditForm = ({ offer }: OfferEditProps) => {
  const { appID } = useTabsCtx();
  const [formData, setFormData] = useState({
    isActive: offer.isActive,
    name: offer.name,
    description: offer.description,
    dealType: offer.tradeType,
    customMetadata: offer.customMetadata ? JSON.stringify(offer.customMetadata, null, 2) : '',
    isPublic: offer.isPublic,
    isPublicStats: offer.isPublicStats
  });
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [openStatusErrorModal, setOpenStatusErrorModal] = useState<boolean>(false);
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);
  const [conflictError, setConflictError] = useState<string>('');
  const navigate = useNavigate();

  const { setBreadcrumbs } = useTabsCtx();

  React.useEffect(() => {
    setBreadcrumbs([
      {
        label: formData?.name,
        to: generatePath(APPS_OFFERS_VIEW_PATH, { appID, id: offer.id })
      },
      {
        label: 'Edit'
      }
    ]);

    return () => setBreadcrumbs([]);
  }, []);

  const {
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(editOfferValidationSchema),
    defaultValues: formData
  });

  const handleInputChange = <K extends keyof typeof formData>(
    key: K,
    value: (typeof formData)[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value
    }));
    // @ts-ignore
    setValue(key, value);

    // @ts-ignore
    clearErrors(key);
  };

  const handleSubmitData = () => {
    updateOffer(appID, offer.id, {
      name: formData.name,
      isActive: formData.isActive,
      description: formData.description,
      tradeType: formData.dealType,
      customMetadata: formData.customMetadata ? JSON.parse(formData.customMetadata) : null,
      isPublic: formData.isPublic,
      isPublicStats: formData.isPublicStats
    })
      .then(() => {
        setOpenSuccessModal(true);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.conflict_error:
              setOpenStatusErrorModal(true);
              setConflictError(error.info?.error.description || error.message);
              break;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to update offer', error);
      });
  };

  const sentData = () => {
    handleSubmit(handleSubmitData)();
  };

  const handleOnDismiss = () => {
    clearErrors();
    navigate(-1);
  };

  const handleCloseSuccessModal = () => {
    setOpenSuccessModal(false);
    setOpenStatusErrorModal(false);
    navigate(generatePath(APPS_OFFERS_PATH, { appID }));
  };

  if (unexpectedError) {
    return (
      <ErrorWindow
        withModal
        title={unexpectedError.code}
        information={unexpectedError.message}
        icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
      />
    );
  }

  return (
    <FormContainer>
      <SuccessEntityWindow
        dismissLabel={'Close'}
        onDismiss={handleCloseSuccessModal}
        isOpen={openSuccessModal}
        icon={<SvgIcon src={CreateSuccessIcon} width={195} height={120} />}
        title="Offer has been updated"
        information="The offer was successfully updated."
      />
      <ErrorEntityWindow
        dismissLabel={'Close'}
        onDismiss={handleCloseSuccessModal}
        isOpen={openStatusErrorModal}
        title={conflictError}
        information={
          <>
            There are some <b>active deals</b> related to this App. They must be deactivated first.
          </>
        }
      />
      <StyledCaption />
      <StyledCaption>
        <BodyText size={1} scale={'lg'}>
          Details
        </BodyText>
      </StyledCaption>
      <StyledSeparator />
      <FlexRow justify="space-between">
        <BodyText size={1}>Active Status</BodyText>
        <Switch
          checked={formData.isActive}
          onChange={() => handleInputChange('isActive', !formData.isActive)}
          onColor={TOGGLE_ACTIVE_COLOR}
          checkedIcon={false}
          uncheckedIcon={false}
        />
      </FlexRow>
      <StyledSeparator />
      <FlexColumn gap={20}>
        <FlexColumn gap={4}>
          <BodyText size={1}>Deal Type</BodyText>
          <ItemArea gap={16}>
            <DealTradeInfoSnippet dealType={offer.tradeType}></DealTradeInfoSnippet>
          </ItemArea>
        </FlexColumn>
        <StyledInput
          required
          label={<BodyText size={1}>Name</BodyText>}
          placeholder={'Offer name here'}
          value={formData.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange('name', e.target.value);
          }}
        />
        {errors?.name?.message && (
          <ErrorTextContainer justify={'flex-start'}>
            <ErrorText size={3}>{errors?.name?.message}</ErrorText>
          </ErrorTextContainer>
        )}
        <Textarea
          label={<BodyText size={1}>Description</BodyText>}
          placeholder={'Offer description here'}
          value={formData.description}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange('description', e.target.value);
          }}
        />
        {errors?.description?.message && (
          <ErrorTextContainer justify={'flex-start'}>
            <ErrorText size={3}>{errors?.description?.message}</ErrorText>
          </ErrorTextContainer>
        )}
        {errors?.dealType?.message && (
          <ErrorTextContainer justify={'flex-start'}>
            <ErrorText size={3}>{errors?.dealType?.message}</ErrorText>
          </ErrorTextContainer>
        )}
        <Textarea
          label={<BodyText size={1}>Custom metadata</BodyText>}
          placeholder={'Custom JSON'}
          value={formData.customMetadata?.toString()}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange('customMetadata', e.target.value);
          }}
        />
        {errors?.customMetadata?.message && (
          <ErrorTextContainer justify={'flex-start'}>
            <ErrorText size={3}>{errors?.customMetadata?.message}</ErrorText>
          </ErrorTextContainer>
        )}
        <Checkbox
          label={<BodyText size={1}>Publish to Catalog</BodyText>}
          checked={formData.isPublic}
          onChange={() => {
            handleInputChange('isPublic', !formData.isPublic);
          }}
        />
        <Checkbox
          label={<BodyText size={1}>Show Statistics</BodyText>}
          checked={formData.isPublicStats}
          onChange={() => {
            handleInputChange('isPublicStats', !formData.isPublicStats);
          }}
        />
      </FlexColumn>
      <StyledButtonRow gap={10}>
        <StyledDismissButton color={'secondaryBlue'} onClick={handleOnDismiss}>
          Cancel
        </StyledDismissButton>
        <StyledConfirmButton color="primaryBlue" onClick={sentData}>
          Save
        </StyledConfirmButton>
      </StyledButtonRow>
    </FormContainer>
  );
};
